import { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';

export interface ModuleSearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
  placeholder?: string;
  resultCount?: number;
  autoFocus?: boolean;
  /** When true, renders its own module-toolbar wrapper (default: true for backwards compat) */
  standalone?: boolean;
  /** When true, renders as disabled placeholder (e.g. not-connected state) */
  disabled?: boolean;
  /** Optional id for the wrapper element (used by MobileSearchToggle) */
  id?: string;
}

export interface ModuleSearchBarRef {
  focus: () => void;
}

/**
 * Reusable search bar component for module filtering.
 * Standard: 36px height, 13px font, white background, 8px border-radius.
 *
 * Usage:
 *   Standalone (renders its own module-toolbar wrapper):
 *     <ModuleSearchBar value={q} onChange={setQ} onClose={() => setQ('')} placeholder="Search..." />
 *
 *   Inside an existing toolbar (e.g. alongside a "+ New" button):
 *     <div className="module-toolbar">
 *       <ModuleSearchBar standalone={false} value={q} onChange={setQ} onClose={() => setQ('')} />
 *       <button>+ New</button>
 *     </div>
 */
export const ModuleSearchBar = forwardRef<ModuleSearchBarRef, ModuleSearchBarProps>(
  ({ value, onChange, onClose, placeholder = 'Search...', resultCount, autoFocus = true, standalone = true, disabled = false, id }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
    }));

    useEffect(() => {
      if (autoFocus && !disabled) {
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    }, [autoFocus, disabled]);

    const searchInput = (
      <div style={{ flex: 1, position: 'relative' }}>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#6b7280"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              onChange('');
              onClose();
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          style={{
            width: '100%',
            height: 36,
            padding: '0 70px 0 34px',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            fontSize: 13,
            outline: 'none',
            background: 'var(--color-surface)',
            color: 'var(--color-text)',
            boxSizing: 'border-box',
            textOverflow: 'ellipsis',
            cursor: disabled ? 'default' : undefined,
          }}
          onFocus={() => {}}
          onBlur={() => {}}
        />
        {!disabled && (
          <div
            style={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {value && resultCount !== undefined && (
              <span style={{ fontSize: 12, color: 'var(--color-text-faint)', whiteSpace: 'nowrap' }}>
                {resultCount} {resultCount === 1 ? 'result' : 'results'}
              </span>
            )}
            {value && (
              <button
                onClick={() => onChange('')}
                title="Clear search"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--color-text-faint)',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
    );

    if (standalone) {
      return <div id={id} className="module-toolbar module-search-toolbar" style={{ paddingLeft: 15, paddingRight: 15 }}>{searchInput}</div>;
    }

    return searchInput;
  }
);

ModuleSearchBar.displayName = 'ModuleSearchBar';

/**
 * Hook to add Cmd/Ctrl+F keyboard shortcut to open search
 */
export function useSearchShortcut(onOpen: () => void) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault();
        onOpen();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onOpen]);
}
